import * as React from 'react'
import { useControlled, useEventCallback, useForkRef } from '../../hooks'
import { ownerDocument, clamp } from '../../utils'
import { Box } from '../../components/Box'
import { type SxProps, sx as sxFunc } from '../../components/sx'
import { Drawer, type DrawerProps } from '../../components/Drawer'
import { Button, type ButtonProps } from '../../components/Button'
import { type BoxProps } from '../../components/Box'
import { type SvgIconProps } from '../../components/SvgIcon'
import { Divider } from '../../components/Divider'
import clsx from 'clsx'
import styles from './index.module.scss'
import ChevronRight from '../../components/internal/svg-icons/ChevronRight'
import ChevronLeft from '../../components/internal/svg-icons/ChevronLeft'

export interface CursorCoordinates {
  x: number
  y: number
}

export interface LayoutClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the content element. */
  content: string
  /** Styles applied to the content element when sidebar is open. */
  sidebarContentOpen: string
  /** Styles applied to the sidebar element. */
  sidebar: string
  /** Styles applied to the sidebar element when sidebar is open */
  sidebarOpen: string
  /** Styles applied to the toggle button element*/
  toggleButton: string
  /** Styles applied to the sidebar wrapper element*/
  sidebarWrapper: string
  /** Styles applied to the sidebar resizer element*/
  sidebarResizer: string
  /** Styles applied to the sidebar resizer element while sidebarVariant = 'permanent' & hideResizerHandle = true */
  sidebarResizerWithoutResizerHandle: string
  /** Styles applied to the sidebar resizer's handle element*/
  sidebarResizerHandle: string
  /** Styles applied to the sidebar resizer's handle element while invisibleResizerHandle = true, sidebarVariant = 'permanent' & hideResizerHandle = false */
  sidebarResizerHandleInvisible: string
  /** Styles applied to the sidebar divier element*/
  sidebarDivider: string
  /** Styles applied to the sidebar content element*/
  sidebarContent: string
  /** Styles applied to the sidebar element while sidebarVariant = 'permanent' */
  sidebarVariantPermanent: string
  /** Styles applied to the sidebar's resizer handle element while hideResizerHandle = true */
  hideResizerHandle: string
  /** Styles applied to the sidebar element while enableSidebarTemporary = true, sidebarVariant = 'persistant' & while user hovers on sidebar(closed state)*/
  sidebarTemporary: string
}

type Direction = 'left' | 'right'
export interface LayoutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default div
   */
  Component?: React.ElementType
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<LayoutClasses>
  /**
   * Sidebar is layout component that allows you to set content on left/right side of the layout.
   */
  sidebar?: JSX.Element
  /**
   * Sidebar Width defines the width of the sidebar.
   */
  sidebarWidth?: number
  /**
   * Side from which the drawer will appear.
   * @default 'left'
   */
  sidebarAnchor?: Direction
  /**
   * The variant to use.
   * @default 'persistent'
   */
  sidebarVariant?: 'permanent' | 'persistent'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The extra props for the slot components.
   * You can override the existing props or add new ones.
   *
   * @default {}
   */
  slotProps?: {
    /** Sidebar provide drawer props to sidebar */
    Sidebar?: Omit<DrawerProps, 'variant' | 'anchor' | 'open'>
    /** Content Box provide box props to content */
    Content?: BoxProps
    /** ToggleButton provide button props to toggle button*/
    ToggleButton?: ButtonProps
    /** OpenIcon provide svg icon props to open icon */
    OpenIcon?: SvgIconProps
    /** CloseIcon provide svg icon props to close icon */
    CloseIcon?: SvgIconProps
  }
  /**
   * The components used for each slot inside.
   *
   * This prop is an alias for the `components` prop, which will be deprecated in the future.
   *
   * @default {}
   */
  slots?: {
    /** Set any other type of component in place of toggle button
     *
     * @default IconButton
     */
    ToggleButton: React.ElementType
    /** Set custom open icon
     *
     * @default IconButton
     */
    OpenIcon: React.ElementType
    /** Set custom close icon
     *
     * @default IconButton
     */
    CloseIcon: React.ElementType
  }
  /**
   * If `true`, the component is shown.
   * @default true
   */
  open?: boolean
  /**
   * Default state of sidebar, If `true`, the component is shown.
   * @default true
   */
  defaultOpen?: boolean
  /**
   * Default state of sidebar width
   * @default 280
   */
  defaultSidebarWidth?: number
  /**
   * If 'true', the resizer handle is hide
   * @default false
   */
  hideResizerHandle?: boolean
  /** If 'true', the resizer handle is hide but also need these props : sidebarVariant = 'permanent' & hideResizerHandle = false
   * @default false
   */
  invisibleResizerHandle?: boolean
  /**
   * If 'true', the sidebar is opent on hover while it is close
   * @default false
   */
  enableSidebarTemporary?: boolean
  /**
   * Callback fired when the state is changed.
   */
  onChange?: (value: boolean) => void
  /**
   * Callback fired when the sidebar width is changed.
   */
  onSidebarWidthChange?: (width: number) => void
  /**
   *
   */
  minWidth?: number
  /**
   *
   */
  maxWidth?: number
}

function computeNewWidth(
  clickX: number,
  columnBounds: DOMRect,
  resizeDirection: Direction
) {
  let newWidth = 0
  if (resizeDirection === 'left') {
    newWidth += clickX - columnBounds.left
  } else {
    newWidth += columnBounds.right - clickX
  }
  return newWidth
}

// TODO: remove support for Safari < 13.
// https://caniuse.com/#search=touch-action
//
// Safari, on iOS, supports touch action since v13.
// Over 80% of the iOS phones are compatible
// in August 2020.
// Utilizing the CSS.supports method to check if touch-action is supported.
// Since CSS.supports is supported on all but Edge@12 and IE and touch-action
// is supported on both Edge@12 and IE if CSS.supports is not available that means that
// touch-action will be supported
let cachedSupportsTouchActionNone = false
function doesSupportTouchActionNone(): boolean {
  if (cachedSupportsTouchActionNone === undefined) {
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      cachedSupportsTouchActionNone = CSS.supports('touch-action', 'none')
    } else {
      cachedSupportsTouchActionNone = true
    }
  }
  return cachedSupportsTouchActionNone
}

function trackFinger(
  event: any,
  currentTouchId: number | undefined
): CursorCoordinates | boolean {
  if (currentTouchId !== undefined && event.changedTouches) {
    for (let i = 0; i < event.changedTouches.length; i += 1) {
      const touch = event.changedTouches[i]
      if (touch.identifier === currentTouchId) {
        return {
          x: touch.clientX,
          y: touch.clientY,
        }
      }
    }

    return false
  }

  return {
    x: event.clientX,
    y: event.clientY,
  }
}

export const Layout = React.forwardRef<HTMLDivElement, LayoutProps>(
  function Layout(props, ref) {
    const {
      className,
      classes,
      children,
      Component = 'div',
      sidebar,
      sidebarWidth: sidebarWidthProp,
      defaultSidebarWidth = 280,
      sidebarAnchor = 'left',
      sidebarVariant = 'persistent',
      slotProps = {},
      slots = {
        ToggleButton: Button,
        OpenIcon: sidebarAnchor === 'left' ? ChevronLeft : ChevronRight,
        CloseIcon: sidebarAnchor === 'left' ? ChevronRight : ChevronLeft,
      },
      sx,
      open: openProp,
      onChange,
      defaultOpen = true,
      hideResizerHandle = false,
      invisibleResizerHandle = false,
      enableSidebarTemporary = false,
      minWidth = 50,
      maxWidth = 500,
      onSidebarWidthChange,
      ...other
    } = props

    const layoutRef = React.useRef<HTMLElement>()
    const layoutForkedRef = useForkRef(layoutRef, ref)
    const resizeHandlerRef = React.useRef<HTMLDivElement>(null)
    const touchIdRef = React.useRef<number>()

    const [open, setOpen] = useControlled({
      controlled: openProp,
      name: 'open',
      default: defaultOpen,
      state: 'open',
    })

    const [sidebarWidth, setSidebarWidth] = useControlled({
      controlled: sidebarWidthProp,
      name: 'sidebarWidth',
      default: defaultSidebarWidth,
      state: 'sidebarWidth',
    })

    const currentWidthRef = React.useRef<number>(sidebarWidth)

    const updateWidth = (newWidth: number) => {
      layoutRef.current?.style.setProperty('--sidebarWidth', `${newWidth}px`)
      currentWidthRef.current = newWidth
    }

    const handleResizeHandlerMouseDown = useEventCallback(
      (event: React.MouseEvent<HTMLElement>) => {
        if (event.button !== 0) {
          return
        }
        event.preventDefault()
        const doc = ownerDocument(layoutRef.current)
        resizeHandlerRef.current!.classList.add(styles.active ?? '')
        layoutRef.current!.classList.add(styles.active ?? '')
        currentWidthRef.current = sidebarWidth
        doc.body.style.cursor = 'col-resize'
        doc.addEventListener('mousemove', handleResizeMouseMove)
        doc.addEventListener('mouseup', handleResizeMouseUp)
      }
    )

    const handleResizeHandlerTouchStart: React.TouchEventHandler<HTMLDivElement> =
      useEventCallback(event => {
        if (!doesSupportTouchActionNone()) {
          event.preventDefault()
        }
        const touch = event.changedTouches[0]
        if (touch !== null) {
          // A number that uniquely identifies the current finger in the touch session.
          touchIdRef.current = touch?.identifier
        }
        const doc = ownerDocument(layoutRef.current)
        resizeHandlerRef.current!.classList.add(styles.active ?? '')
        layoutRef.current!.classList.add(styles.active ?? '')
        currentWidthRef.current = sidebarWidth
        doc.addEventListener('touchmove', handleTouchMove)
        doc.addEventListener('touchend', handleTouchEnd)
      })

    const handleResizeMouseMove = useEventCallback(
      (nativeEvent: MouseEvent) => {
        if (nativeEvent.buttons === 0) {
          handleResizeMouseUp()
        }
        let newWidth = computeNewWidth(
          nativeEvent.clientX,
          layoutRef.current!.getBoundingClientRect(),
          sidebarAnchor
        )
        newWidth = clamp(newWidth, minWidth, maxWidth)
        updateWidth(newWidth)
      }
    )

    const handleTouchMove = useEventCallback((nativeEvent: TouchEvent) => {
      const finger = trackFinger(nativeEvent, touchIdRef.current)
      if (!finger) {
        return
      }
      // @ts-ignore
      if (nativeEvent.type === 'mousemove' && nativeEvent.buttons === 0) {
        handleTouchEnd(nativeEvent)
        return
      }

      let newWidth = computeNewWidth(
        (finger as CursorCoordinates).x,
        layoutRef.current!.getBoundingClientRect(),
        sidebarAnchor
      )
      newWidth = clamp(newWidth, minWidth, maxWidth)
      updateWidth(newWidth)
    })

    const handleResizeMouseUp = useEventCallback(() => {
      stopListening()
      setSidebarWidth(currentWidthRef.current)
      if (onSidebarWidthChange) {
        onSidebarWidthChange(currentWidthRef.current)
      }
    })

    const handleTouchEnd = useEventCallback((nativeEvent: TouchEvent) => {
      const finger = trackFinger(nativeEvent, touchIdRef.current)
      if (!finger) {
        return
      }
      stopListening()
      setSidebarWidth(currentWidthRef.current)
      if (onSidebarWidthChange) {
        onSidebarWidthChange(currentWidthRef.current)
      }
    })

    const stopListening = React.useCallback(() => {
      const doc = ownerDocument(layoutRef.current)
      doc.body.style.removeProperty('cursor')
      resizeHandlerRef.current!.classList.remove(styles.active ?? '')
      layoutRef.current!.classList.remove(styles.active ?? '')
      doc.removeEventListener('mousemove', handleResizeMouseMove)
      doc.removeEventListener('mouseup', handleResizeMouseUp)
      doc.removeEventListener('touchmove', handleTouchMove)
      doc.removeEventListener('touchend', handleTouchEnd)
    }, [
      handleResizeMouseMove,
      handleResizeMouseUp,
      handleTouchMove,
      handleTouchEnd,
    ])

    const handleDrawerToggle = useEventCallback((value: boolean) => {
      if (enableSidebarTemporary && value === false) {
        setTimeout(() => {
          layoutRef.current!.classList.add(styles.forceCloseSidebar ?? '')
        }, 10)
        setTimeout(() => {
          layoutRef.current!.classList.remove(styles.forceCloseSidebar ?? '')
        }, 100)
      }
      setOpen(value)
      if (typeof onChange === 'function') {
        onChange(value)
      }
    })
    const drawer = (
      <Drawer
        {...slotProps?.Sidebar}
        variant="permanent"
        anchor={sidebarAnchor}
        open={open}
        className={clsx(
          styles.sidebar,
          classes?.sidebar,
          styles[sidebarAnchor],
          (open || sidebarVariant === 'permanent') && [
            styles.open,
            classes?.sidebarOpen,
          ],
          slotProps?.Sidebar?.className
        )}
        PaperProps={{
          ...slotProps?.Sidebar?.PaperProps,
          sx: {
            ...slotProps?.Sidebar?.PaperProps?.sx,
            ...(sidebarVariant !== 'permanent' && { position: 'absolute' }),
            width: 100,
            border: 0,
            bgColor: 'transparent',
          },
        }}
      >
        <Box className={clsx(styles.sidebarWrapper, classes?.sidebarWrapper)}>
          <Box className={clsx(styles.sidebarContent, classes?.sidebarContent)}>
            {sidebar}
          </Box>
          <Box
            className={clsx(
              styles.sidebarResizer,
              classes?.sidebarResizer,
              sidebarVariant === 'permanent' &&
                hideResizerHandle && [
                  styles.sidebarResizerWithoutResizerHandle,
                  classes?.sidebarResizerWithoutResizerHandle,
                ]
            )}
          >
            <Box
              ref={resizeHandlerRef}
              onMouseDown={handleResizeHandlerMouseDown}
              onTouchStart={handleResizeHandlerTouchStart}
              className={clsx(
                styles.sidebarResizerHandle,
                classes?.sidebarResizerHandle,
                hideResizerHandle && [
                  styles.hideResizerHandle,
                  classes?.hideResizerHandle,
                ],
                invisibleResizerHandle &&
                  sidebarVariant === 'permanent' &&
                  !hideResizerHandle && [
                    styles.sidebarResizerHandleInvisible,
                    classes?.sidebarResizerHandleInvisible,
                  ]
              )}
            >
              <Divider
                orientation="vertical"
                color="secondary"
                className={clsx(styles.sidebarDivider, classes?.sidebarDivider)}
              ></Divider>
            </Box>
            {sidebarVariant !== 'permanent' && (
              <slots.ToggleButton
                onClick={() => handleDrawerToggle(!open)}
                {...slotProps?.ToggleButton}
                color="secondary"
                size="small"
                className={clsx(
                  styles.toggleButton,
                  classes?.toggleButton,
                  slotProps?.ToggleButton?.className
                )}
              >
                {open ? (
                  <slots.OpenIcon {...slotProps?.OpenIcon} />
                ) : (
                  <slots.CloseIcon {...slotProps?.CloseIcon} />
                )}
              </slots.ToggleButton>
            )}
          </Box>
        </Box>
      </Drawer>
    )
    return (
      <Component
        className={clsx(
          styles.root,
          classes?.root,
          !open &&
            sidebarVariant !== 'permanent' &&
            enableSidebarTemporary && [
              styles.sidebarTemporary,
              classes?.sidebarTemporary,
            ],
          className,
          sxFunc(sx)
        )}
        ref={layoutForkedRef}
        {...other}
        //@ts-ignore
        style={{ ...other.style, '--sidebarWidth': `${sidebarWidth}px` }}
      >
        {sidebar && drawer}
        <Box
          {...slotProps?.Content}
          className={clsx(
            styles.content,
            classes?.content,
            sidebar && styles[sidebarAnchor],
            sidebarVariant === 'permanent' && [
              styles.sidebarVariantPermanent,
              classes?.sidebarVariantPermanent,
            ],
            sidebar &&
              (open || sidebarVariant === 'permanent') && [
                styles.open,
                classes?.sidebarContentOpen,
              ],
            slotProps?.Content?.className
          )}
        >
          {children}
        </Box>
      </Component>
    )
  }
)
